import { useState, useEffect, useMemo } from 'react';
import { palette, admin, transition } from '../../styles/tokens';
import { SETTINGS_SECTIONS } from './config/settingsConfig';
import { useSettingsCompletion } from './hooks/useSettingsCompletion';
import { useTrialIntelligence } from './hooks/useTrialIntelligence';
import { SettingsSection } from './components/SettingsSection';
import { SettingsProgressBar } from './components/SettingsProgressBar';
import type { CompletionStatus } from './hooks/useSettingsCompletion';

type Filter = 'all' | 'needs_setup' | 'done';

function resolveStatus(
  completionKey: string,
  areas: Record<string, CompletionStatus>,
): CompletionStatus {
  if (completionKey === 'optional') return 'optional';
  return areas[completionKey] ?? 'needs_setup';
}

export function SettingsHub() {
  const { areas, percentage, needsAttentionCount, loading } = useSettingsCompletion();
  const trial = useTrialIntelligence(percentage, needsAttentionCount);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => { document.title = 'CrewBit \u00b7 Settings'; }, []);

  // Compute counts for tab badges
  const counts = useMemo(() => {
    let needsSetup = 0;
    let done = 0;
    for (const section of SETTINGS_SECTIONS) {
      for (const card of section.cards) {
        const status = resolveStatus(card.completionKey, areas);
        if (status === 'needs_setup') needsSetup++;
        else if (status === 'done') done++;
      }
    }
    return { needsSetup, done };
  }, [areas]);

  // Filter sections — returns sections with filtered card arrays
  const filteredSections = useMemo(() => {
    if (filter === 'all') {
      return SETTINGS_SECTIONS.map(s => ({ ...s }));
    }
    return SETTINGS_SECTIONS.map(section => ({
      ...section,
      cards: section.cards.filter(card => {
        const status = resolveStatus(card.completionKey, areas);
        return status === filter;
      }),
    })).filter(section => section.cards.length > 0);
  }, [filter, areas]);

  const TABS: { key: Filter; label: string; count?: number }[] = [
    { key: 'all', label: 'All' },
    { key: 'needs_setup', label: 'Needs setup', count: counts.needsSetup },
    { key: 'done', label: 'Done', count: counts.done },
  ];

  if (loading) {
    return (
      <div>
        <div style={{
          fontSize: 13,
          color: admin.caption,
          marginTop: 4,
          marginBottom: 24,
        }}>
          Manage your business profile, integrations, and preferences
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 12,
        }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} style={{
              height: 88,
              borderRadius: 12,
              background: palette.g100,
              animation: 'crewbit-fadein 150ms ease',
            }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        fontSize: 13,
        color: admin.caption,
        marginBottom: 24,
      }}>
        Manage your business profile, integrations, and preferences
      </div>

      {trial.shouldShow && (
        <SettingsProgressBar
          percentage={percentage}
          message={trial.message}
          subMessage={trial.subMessage}
          urgency={trial.urgency}
          canExtend={trial.canExtend}
          onExtend={trial.extendTrial}
        />
      )}

      {/* Filter tabs */}
      <style>{`
        .crewbit-settings-filter-tab { outline: none; }
        .crewbit-settings-filter-tab:focus-visible {
          outline: 2px solid ${palette.accent};
          outline-offset: 2px;
        }
      `}</style>
      <div style={{
        display: 'flex',
        gap: 0,
        background: palette.g100,
        borderRadius: 8,
        padding: 2,
        marginBottom: 20,
        width: 'fit-content',
      }}>
        {TABS.map(t => {
          const active = filter === t.key;
          return (
            <button
              key={t.key}
              className="crewbit-settings-filter-tab"
              onClick={() => setFilter(t.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 16px',
                borderRadius: 5,
                border: 'none',
                cursor: 'pointer',
                fontSize: 12.5,
                fontWeight: 500,
                fontFamily: 'inherit',
                background: active ? palette.g800 : 'transparent',
                color: active ? palette.white : palette.g500,
                transition: `all ${transition.fast}`,
              }}
            >
              {t.label}
              {t.count != null && t.count > 0 && (
                <span style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  padding: '1px 6px',
                  borderRadius: 8,
                  background: active ? 'rgba(255,255,255,0.2)' : palette.g200,
                  color: active ? palette.white : palette.g500,
                }}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {filteredSections.length === 0 ? (
        <div style={{
          padding: '40px 0',
          textAlign: 'center',
          fontSize: 13.5,
          color: palette.g400,
        }}>
          {filter === 'needs_setup' ? 'Everything is set up.' : 'No completed items yet.'}
        </div>
      ) : (
        filteredSections.map(section => (
          <SettingsSection
            key={section.id}
            label={section.label}
            cards={section.cards}
            completionAreas={areas}
          />
        ))
      )}
    </div>
  );
}
